import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import { Icons } from '@components/common';
import SideBarGroups from '@components/common/SideBar/SideBarGroups';

import { common } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { resetAlarmManagementTable } from '@slices/alarmManagement';
import { resetAllSitesDBTable } from '@slices/allCells';
import { resetApproveUnit } from '@slices/approveUnit';
import { resetAssetsTable } from '@slices/assets';
import { resetAssignApprovalUnit } from '@slices/assignApprovalUnit';
import { setIsAdminSidebarOpen } from '@slices/common';
import { resetCrowdCellsTable } from '@slices/crowdCells';
import { resetDashboardPOAssetTypesTable } from '@slices/dashboardPOAssetTypesTable';
import { resetDashboardRequestsTable } from '@slices/dashboardPORequestsTable';
import { resetDashboardPOStatusesTable } from '@slices/dashboardPOStatusesTable';
import { resetDataSourceGNBConfiguration } from '@slices/dataSourceGNBConfiguration';
import { resetErrorCodesTable } from '@slices/errors';
import { resetOrganisationsTable } from '@slices/organisations';
import { resetManageSitesDBTable } from '@slices/sites';
import { resetUserHistory } from '@slices/userHistory';
import { resetUsersTable } from '@slices/users';

import { IMenuItem } from './interfaces';

import styles from './SideBar.module.scss';

interface ISideBarProps {
	title: string;
	setIsFullSizeSideBar: React.Dispatch<React.SetStateAction<boolean>>;
	sideBarItems: IMenuItem[];
}

const SideBar = ({ title, setIsFullSizeSideBar, sideBarItems }: ISideBarProps) => {
	const { isKaiAssistantLoaded } = useAppSelector(common);

	const [isOpenSideBar, setIsOpenSideBar] = useState(false);
	const [openedGroups, setOpenedGroups] = useState<string[]>([]);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const sideBarClasses = cn(styles.sideBar, {
		[styles.opened]: isOpenSideBar,
		[styles.withKaiAssistant]: isKaiAssistantLoaded,
	});

	const toggleSideBar = useCallback(() => {
		setIsOpenSideBar(prevState => !prevState);
		setIsFullSizeSideBar(prevState => !prevState);
	}, [setIsFullSizeSideBar, setIsOpenSideBar]);

	const openSideBar = useCallback(() => {
		setIsOpenSideBar(true);
		setIsFullSizeSideBar(true);
	}, [setIsFullSizeSideBar]);

	const toggleMenu = (title: string) => {
		const isTitleIncluded = openedGroups.includes(title);

		if (isTitleIncluded && isOpenSideBar) {
			setOpenedGroups(openedGroups.filter((menu) => menu !== title));
		} else if (!isTitleIncluded) {
			setOpenedGroups([...openedGroups, title]);
		}
	};
	const handleRouteNavigation = useCallback((route: string, isActive?: boolean) => {
		if (!isActive) {
			dispatch(resetDashboardPOAssetTypesTable());
			dispatch(resetDashboardPOStatusesTable());
			dispatch(resetDashboardRequestsTable());
			dispatch(resetUsersTable());
			dispatch(resetOrganisationsTable());
			dispatch(resetAssetsTable());
			dispatch(resetCrowdCellsTable());
			dispatch(resetDataSourceGNBConfiguration());
			dispatch(resetErrorCodesTable());
			dispatch(resetUserHistory());
			dispatch(resetManageSitesDBTable());
			dispatch(resetAllSitesDBTable());
			dispatch(resetApproveUnit());
			dispatch(resetAssignApprovalUnit());
			dispatch(resetAlarmManagementTable());
		}

		navigate(route);
	}, [dispatch, navigate]);

	useEffect(() => {
		dispatch(setIsAdminSidebarOpen(isOpenSideBar));
	}, [dispatch, isOpenSideBar]);

	return (
		<div className={sideBarClasses}>
			<div
				className={styles.sideBarToggle}
				onClick={toggleSideBar}
			>
				<div className={styles.title}>{title}</div>
				<div className={styles.chevrons}>{Icons.chevrons()}</div>
			</div>
			<div className={styles.menuList}>
				{sideBarItems.map((menuGroup, index) => {
					return (
						<SideBarGroups
							key={`${menuGroup.title}-${index}`}
							handleRouteNavigation={handleRouteNavigation}
							isOpenSideBar={isOpenSideBar}
							menuGroup={menuGroup}
							openSideBar={openSideBar}
							openedGroups={openedGroups}
							toggleMenu={toggleMenu}
						/>
					);
				})}
			</div>
		</div>
	);
};

export default SideBar;
